// Leasing: applications, screening decisions, and tenancies.
//
// The lease form is the sharp end of the whole module. Everything the rule stack
// resolved for the property is enforced here at drafting time, because Chicago
// voids a defective clause rather than merely trimming the overcharge — so the
// screen shows the caps BEFORE the figure is typed, not after it is refused.
//
// The decline form is the other sharp end. Declining on a consumer report triggers
// a federal adverse action duty; "we always send them" is not a defence, the file
// is what gets produced. So the notice date and the reporting agency are collected
// as part of the decision rather than chased afterwards.

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import ScreeningPanel from '../../components/secure/pm/ScreeningPanel';
import {
    PageHeader, Panel, Tabs, Stat, Pill, Table, Modal, Input, Select, Textarea,
    Button, Notice, Loading, money, fmtDate, titleCase, statusTone,
} from '../../components/secure/pm/PMKit';
import ScreeningCriteriaPanel from '../../components/secure/pm/ScreeningCriteriaPanel';
import { getCurrentUser } from '../../hooks/useCurrentUser';

export default function PMLeasingPage() {
    const apiClient = useApiClient();
    // Tenant screening lives on the applications tab, and it is a feature people
    // look for by name. Reading the tab from the URL lets the menu link straight
    // to it instead of landing on tenancies and hoping somebody clicks across.
    const [params, setParams] = useSearchParams();
    const [tab, setTab] = useState(['applications', 'criteria'].includes(params.get('tab')) ? params.get('tab') : 'leases');
    const changeTab = (next) => {
        setTab(next);
        // Keep the URL honest so the tab survives a refresh and can be linked.
        if (next !== 'leases') setParams({ tab: next }, { replace: true });
        else setParams({}, { replace: true });
    };
    const [loading, setLoading] = useState(true);
    const [leases, setLeases] = useState([]);
    const [apps, setApps] = useState([]);
    const [properties, setProperties] = useState([]);
    const [units, setUnits] = useState([]);
    const [detail, setDetail] = useState(null);
    const [cap, setCap] = useState(null);
    const [modal, setModal] = useState(null);
    const [screeningId, setScreeningId] = useState(null);
    const [form, setForm] = useState({});
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const [l, a, p] = await Promise.all([
                apiClient.get('/api/property-management/leases'),
                apiClient.get('/api/property-management/applications'),
                apiClient.get('/api/property-management/properties'),
            ]);
            setLeases(l || []); setApps(a || []); setProperties(p || []);
        } catch (e) { setError({ message: e.message }); } finally { setLoading(false); }
    }, [apiClient]);

    useEffect(() => { load(); }, [load]);

    // Choosing the property loads its units and its resolved late-fee cap, so the
    // limits are on screen while the figures are being entered.
    const chooseProperty = async (id) => {
        setForm((f) => ({ ...f, pm_property_id: id, unit_id: '' }));
        setCap(null); setUnits([]);
        if (!id) return;
        try {
            const d = await apiClient.get(`/api/property-management/properties/${id}`);
            setUnits(d.units || []);
            setForm((f) => ({ ...f, _stack: d.rule_stack }));
        } catch (e) { setError({ message: e.message }); }
    };

    const checkCap = async (rent) => {
        if (!form.pm_property_id || !rent) { setCap(null); return; }
        try {
            setCap(await apiClient.post(
                `/api/property-management/properties/${form.pm_property_id}/late-fee-check`,
                { monthly_rent: rent }));
        } catch { setCap(null); }
    };

    const submit = async (path, body, after) => {
        setBusy(true); setError(null);
        try {
            const r = await apiClient.post(path, body);
            setModal(null); setForm({}); setCap(null);
            await load();
            if (after) after(r);
            else if (r?.deadlines_created !== undefined) {
                setError({ tone: 'good', title: 'Tenancy created',
                    message: `${r.attachments_required} required attachment(s) and ${r.deadlines_created} `
                           + `statutory deadline(s) generated under ${(r.layers_applied || []).join(' + ')}.` });
            }
        } catch (e) {
            setError({ message: e.message, problems: e.problems || e.requires });
        } finally { setBusy(false); }
    };

    const openLease = async (id) => {
        try { setDetail(await apiClient.get(`/api/property-management/leases/${id}`)); setModal('lease-detail'); }
        catch (e) { setError({ message: e.message }); }
    };

    if (loading) return <Loading />;

    const eff = form._stack?.effective || {};

    return (
        <div>
            <PageHeader
                title="Leasing"
                subtitle="A lease is validated against the rules in force when it is signed, and the resolved stack is then frozen onto it."
            >
                <Button variant="ghost" onClick={() => { setForm({}); setModal('application'); }}>Take application</Button>
                <Button onClick={() => { setForm({}); setCap(null); setModal('lease'); }}>New tenancy</Button>
            </PageHeader>

            {error && <Notice tone={error.tone || 'bad'} title={error.title}
                message={error.message} problems={error.problems} onDismiss={() => setError(null)} />}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <Stat label="Active tenancies" value={leases.filter((l) => l.status === 'active').length} />
                <Stat label="Ending in 120 days"
                    value={leases.filter((l) => l.end_date &&
                        (new Date(l.end_date) - new Date()) / 86400000 < 120 && l.status === 'active').length}
                    tone="warn" sub="notice windows open" />
                <Stat label="Applications open"
                    value={apps.filter((a) => ['received', 'screening'].includes(a.status)).length} />
                <Stat label="Declined on a report"
                    value={apps.filter((a) => a.adverse_action_required).length}
                    sub="FCRA notice required" />
            </div>

            <Tabs tabs={[
                { key: 'leases', label: 'Tenancies', count: leases.length },
                { key: 'applications', label: 'Applications', count: apps.length },
                { key: 'criteria', label: 'Screening criteria' },
            ]} active={tab} onChange={changeTab} />

            {/* Publishing is the property manager's under the API; the server decides. */}
            {tab === 'criteria' && <ScreeningCriteriaPanel canPublish={getCurrentUser()?.role !== 'examiner'} />}

            {tab === 'leases' && (
                <Panel>
                    <Table
                        columns={[
                            { key: 'property_name', label: 'Property / unit',
                                render: (r) => (
                                    <div>
                                        <div className="text-slate-800 font-medium">{r.property_name}</div>
                                        <div className="text-xs text-slate-400">
                                            Unit {r.unit_number} · <Pill tone={r.use_type === 'commercial' ? 'violet' : 'slate'}>
                                                {r.use_type}</Pill>
                                        </div>
                                    </div>
                                ) },
                            { key: 'tenants', label: 'Tenants', render: (r) => r.tenants || <span className="text-slate-400">none linked</span> },
                            { key: 'term', label: 'Term', nowrap: true,
                                render: (r) => <span className="text-xs">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</span> },
                            { key: 'rent_amount', label: 'Rent', align: 'right', render: (r) => money(r.rent_amount) },
                            { key: 'security_deposit', label: 'Deposit', align: 'right', render: (r) => money(r.security_deposit) },
                            { key: 'status', label: 'Status', nowrap: true,
                                render: (r) => <Pill tone={statusTone(r.status)}>{titleCase(r.status)}</Pill> },
                        ]}
                        rows={leases.map((l) => ({ ...l, _key: l.lease_id }))}
                        onRowClick={(r) => openLease(r.lease_id)}
                        empty="No tenancies. A lease needs a unit, and the property needs an executed management agreement."
                    />
                </Panel>
            )}

            {tab === 'applications' && (
                <Panel>
                    <Table
                        columns={[
                            { key: 'applicant_name', label: 'Applicant',
                                render: (r) => (
                                    <div>
                                        <div className="text-slate-800">{r.applicant_name}</div>
                                        <div className="text-xs text-slate-400">
                                            {r.property_name} · unit {r.unit_number}
                                            {r.asset_class && r.asset_class !== 'residential' && (
                                                <> · <Pill tone="violet">{r.asset_class}</Pill></>
                                            )}
                                        </div>
                                    </div>
                                ) },
                            { key: 'stated_income', label: 'Stated income', align: 'right',
                                render: (r) => (r.asset_class && r.asset_class !== 'residential'
                                    ? <span className="text-slate-300 text-xs">business</span> : money(r.stated_income)) },
                            { key: 'created_at', label: 'Received', nowrap: true,
                                render: (r) => fmtDate(r.created_at) },
                            { key: 'fcra', label: 'FCRA', nowrap: true,
                                render: (r) => r.adverse_action_required
                                    ? (r.adverse_action_sent_on
                                        ? <Pill tone="good">notice {fmtDate(r.adverse_action_sent_on)}</Pill>
                                        : <Pill tone="bad">notice due</Pill>)
                                    : <span className="text-slate-300 text-xs">—</span> },
                            { key: 'status', label: 'Status', nowrap: true,
                                render: (r) => <Pill tone={statusTone(r.status)}>{titleCase(r.status)}</Pill> },
                            { key: 'act', label: '', nowrap: true,
                                // A business file stays openable after the decision: the letter,
                                // the FCRA notice and the documents live on the screening panel.
                                render: (r) => (['received', 'screening'].includes(r.status)
                                        || (r.asset_class && r.asset_class !== 'residential'))
                                    ? <Button variant="ghost"
                                        onClick={() => setScreeningId(
                                            screeningId === r.application_id ? null : r.application_id)}>
                                        {screeningId === r.application_id ? 'Close'
                                            : ['received', 'screening'].includes(r.status) ? 'Screen' : 'Open'}</Button>
                                    : null },
                        ]}
                        rows={apps.map((a) => ({ ...a, _key: a.application_id }))}
                        empty="No applications."
                    />
                    {screeningId && (
                        <div className="mt-4">
                            <ScreeningPanel applicationId={screeningId} onChanged={load} />
                        </div>
                    )}
                </Panel>
            )}

            {/* ---------------- new tenancy ---------------- */}
            {modal === 'lease' && (
                <Modal title="New tenancy"
                    subtitle="The lease takes its type from the unit's use, not the property's class."
                    onClose={() => setModal(null)} wide>
                    <div className="grid sm:grid-cols-2 gap-x-4">
                        <Select label="Property" value={form.pm_property_id || ''}
                            onChange={(e) => chooseProperty(e.target.value)}
                            options={[{ value: '', label: 'Select…' },
                                ...properties.filter((p) => p.status === 'managed')
                                    .map((p) => ({ value: p.pm_property_id, label: p.name }))]}
                            hint="Only managed properties appear — an executed agreement is required first." />
                        <Select label="Unit" value={form.unit_id || ''}
                            onChange={(e) => setForm({ ...form, unit_id: e.target.value })}
                            options={[{ value: '', label: 'Select…' },
                                ...units.map((u) => ({ value: u.unit_id, label: `${u.unit_number} (${u.use_type})` }))]} />
                    </div>

                    {form._stack && (
                        <div className="bg-slate-50 border border-slate-200 rounded p-3 mb-4 text-xs">
                            <div className="font-semibold text-slate-600 mb-1">Resolved for this property</div>
                            <div className="grid sm:grid-cols-3 gap-2 text-slate-600">
                                <div>Deposit cap: <strong>{eff.deposit_cap_months ? `${eff.deposit_cap_months}× rent` : 'none'}</strong></div>
                                <div>Escrow waiver: <strong>{Number(eff.deposit_waiver_allowed) ? 'permitted' : 'prohibited'}</strong></div>
                                <div>Bank disclosure: <strong>{titleCase(eff.deposit_bank_disclosure)}</strong></div>
                            </div>
                        </div>
                    )}

                    <div className="grid sm:grid-cols-2 gap-x-4">
                        <Input label="Start date" type="date" value={form.start_date || ''}
                            onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                        <Input label="End date" type="date" value={form.end_date || ''}
                            onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                        <Input label="Monthly rent" type="number" step="0.01" value={form.rent_amount || ''}
                            onChange={(e) => { setForm({ ...form, rent_amount: e.target.value }); checkCap(e.target.value); }} />
                        <Input label="Security deposit" type="number" step="0.01" value={form.security_deposit || ''}
                            onChange={(e) => setForm({ ...form, security_deposit: e.target.value })}
                            hint={eff.deposit_cap_months && form.rent_amount
                                ? `Maximum ${money(Number(eff.deposit_cap_months) * Number(form.rent_amount))}`
                                : undefined} />
                        <Input label="Late fee" type="number" step="0.01" value={form.late_fee_value ?? ''}
                            onChange={(e) => setForm({ ...form, late_fee_value: e.target.value })}
                            hint={cap?.cap
                                ? (cap.cap.allowed === null
                                    ? cap.cap.basis
                                    : `Maximum ${money(cap.cap.allowed)} — ${cap.cap.basis}`)
                                : 'Enter the rent to see the cap.'} />
                        <Input label="Rent due day" type="number" min="1" max="31" value={form.rent_due_day ?? 1}
                            onChange={(e) => setForm({ ...form, rent_due_day: e.target.value })} />
                        <Input label="Deposit bank — name" value={form.deposit_bank_name || ''}
                            onChange={(e) => setForm({ ...form, deposit_bank_name: e.target.value })} />
                        <Input label="Deposit bank — street address" value={form.deposit_bank_address || ''}
                            onChange={(e) => setForm({ ...form, deposit_bank_address: e.target.value })}
                            hint={eff.deposit_bank_disclosure === 'name_and_address'
                                ? 'Required here. A city alone is legally insufficient.' : undefined} />
                        <Select label="Status" value={form.status || 'draft'}
                            onChange={(e) => setForm({ ...form, status: e.target.value })}
                            options={['draft', 'pending', 'active']} />
                        <Input label="Move in" type="date" value={form.move_in_on || ''}
                            onChange={(e) => setForm({ ...form, move_in_on: e.target.value })} />
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button disabled={busy} onClick={() => {
                            const { _stack, pm_property_id, ...body } = form;
                            submit('/api/property-management/leases', body);
                        }}>{busy ? 'Creating…' : 'Create tenancy'}</Button>
                    </div>
                </Modal>
            )}

            {/* ---------------- application ---------------- */}
            {modal === 'application' && (
                <Modal title="Take an application" onClose={() => setModal(null)}>
                    {/* The class decides which screening law applies: residential runs the
                        Illinois / Cook County sequence, commercial and industrial are underwritten
                        as a business. It follows the unit's use, as the lease type does. */}
                    <Select label="Class of space" value={form.asset_class || 'residential'}
                        onChange={(e) => setForm({ ...form, asset_class: e.target.value, unit_id: '' })}
                        options={[{ value: 'residential', label: 'Residential — a household' },
                            { value: 'commercial', label: 'Commercial — a business (office, retail…)' },
                            { value: 'industrial', label: 'Industrial — a business (warehouse, manufacturing…)' }]} />
                    <div className="grid sm:grid-cols-2 gap-x-4">
                        <Select label="Property" value={form.pm_property_id || ''}
                            onChange={(e) => chooseProperty(e.target.value)}
                            options={[{ value: '', label: 'Select…' },
                                ...properties.filter((p) => p.status === 'managed')
                                    .map((p) => ({ value: p.pm_property_id, label: p.name }))]} />
                        <Select label="Unit" value={form.unit_id || ''}
                            onChange={(e) => setForm({ ...form, unit_id: e.target.value })}
                            options={[{ value: '', label: 'Select…' },
                                ...units.filter((u) => ((form.asset_class || 'residential') === 'residential'
                                    ? u.use_type !== 'commercial' : u.use_type === 'commercial'))
                                    .map((u) => ({ value: u.unit_id, label: u.unit_number }))]}
                            hint={(form.asset_class || 'residential') === 'residential'
                                ? 'Residential units only.' : 'Commercial-use units only.'} />
                        <Input label={(form.asset_class || 'residential') === 'residential' ? 'Applicant name' : 'Business legal name'}
                            value={form.applicant_name || ''}
                            onChange={(e) => setForm({ ...form, applicant_name: e.target.value })} />
                        <Input label="Email" type="email" value={form.email || ''}
                            onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        {(form.asset_class || 'residential') === 'residential' ? (
                            <>
                                <Input label="Stated income" type="number" value={form.stated_income || ''}
                                    onChange={(e) => setForm({ ...form, stated_income: e.target.value })} />
                                <Input label="Criteria version" value={form.criteria_version || ''}
                                    onChange={(e) => setForm({ ...form, criteria_version: e.target.value })}
                                    hint="Uniform written criteria applied to every applicant is the fair housing defence." />
                            </>
                        ) : (
                            <>
                                <Input label="Phone" value={form.phone || ''}
                                    onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                                <Input label="Business address" value={form.current_address || ''}
                                    onChange={(e) => setForm({ ...form, current_address: e.target.value })}
                                    className="sm:col-span-2"
                                    hint="Entity details, financials, guarantors and references are taken on the screening panel; the criteria version in force is bound when it is underwritten." />
                            </>
                        )}
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button disabled={busy} onClick={() => {
                            const { _stack, pm_property_id, ...body } = form;
                            submit('/api/property-management/applications', body);
                        }}>{busy ? 'Saving…' : 'Record application'}</Button>
                    </div>
                </Modal>
            )}

            {/* ---------------- decision ---------------- */}
            {modal === 'decide' && (
                <Modal title="Decide this application"
                    subtitle="A decline resting on a consumer report triggers a federal adverse action notice."
                    onClose={() => setModal(null)}>
                    <Select label="Decision" value={form.status}
                        onChange={(e) => setForm({ ...form, status: e.target.value })}
                        options={['approved', 'conditionally_approved', 'declined', 'withdrawn']} />
                    <Textarea label="Reason" value={form.decision_reason || ''}
                        onChange={(e) => setForm({ ...form, decision_reason: e.target.value })} />
                    <Select label="Was a consumer report relied on?" value={form.consumer_report_used ? 1 : 0}
                        onChange={(e) => setForm({ ...form, consumer_report_used: Number(e.target.value) === 1 })}
                        options={[{ value: 0, label: 'No' }, { value: 1, label: 'Yes' }]} />
                    {form.status === 'declined' && form.consumer_report_used && (
                        <>
                            <Notice tone="warn"
                                message="Declining on a consumer report requires an adverse action notice naming the agency that supplied it, so the applicant can dispute it." />
                            <div className="grid sm:grid-cols-2 gap-x-4">
                                <Input label="Adverse action notice sent" type="date"
                                    value={form.adverse_action_sent_on || ''}
                                    onChange={(e) => setForm({ ...form, adverse_action_sent_on: e.target.value })} />
                                <Select label="Method" value={form.adverse_action_method || 'mail'}
                                    onChange={(e) => setForm({ ...form, adverse_action_method: e.target.value })}
                                    options={['mail', 'email', 'in_person']} />
                                <Input label="Reporting agency" value={form.reporting_agency || ''}
                                    onChange={(e) => setForm({ ...form, reporting_agency: e.target.value })}
                                    className="sm:col-span-2" />
                            </div>
                        </>
                    )}
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button disabled={busy} onClick={() => {
                            const { application_id, ...body } = form;
                            submit(`/api/property-management/applications/${application_id}/decide`, body,
                                () => setError({ tone: 'good', message: 'Decision recorded.' }));
                        }}>{busy ? 'Saving…' : 'Record decision'}</Button>
                    </div>
                </Modal>
            )}

            {/* ---------------- lease detail ---------------- */}
            {modal === 'lease-detail' && detail && (
                <Modal title={`Tenancy — unit ${detail.unit_id}`}
                    subtitle={`${titleCase(detail.lease_type)} · ${fmtDate(detail.start_date)} → ${fmtDate(detail.end_date)}`}
                    onClose={() => { setModal(null); setDetail(null); }} wide>
                    <div className="grid sm:grid-cols-4 gap-3 mb-5">
                        <Stat label="Rent" value={money(detail.rent_amount)} />
                        <Stat label="Deposit" value={money(detail.security_deposit)} />
                        <Stat label="Late fee" value={money(detail.late_fee_value)}
                            sub={titleCase(detail.late_fee_model)} />
                        <Stat label="Status" value={titleCase(detail.status)} />
                    </div>

                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                        Required attachments
                    </h4>
                    <Table
                        columns={[
                            { key: 'label', label: 'Document' },
                            { key: 'authority', label: 'Required by' },
                            { key: 'delivered', label: 'Delivered', nowrap: true,
                                render: (r) => r.delivered
                                    ? <Pill tone="good">{fmtDate(r.delivered_on)}</Pill>
                                    : <Button variant="ghost" onClick={async () => {
                                        await apiClient.post(
                                            `/api/property-management/leases/${detail.lease_id}/attachments/${r.attachment_key}/delivered`,
                                            { tenant_acknowledged: true });
                                        openLease(detail.lease_id);
                                    }}>Mark delivered</Button> },
                        ]}
                        rows={(detail.attachments || []).map((a) => ({ ...a, _key: a.attachment_id }))}
                        empty="None required beyond the base state set."
                    />

                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mt-6 mb-2">
                        Statutory deadlines
                    </h4>
                    <Table
                        columns={[
                            { key: 'due_on', label: 'Due', nowrap: true, render: (r) => fmtDate(r.due_on) },
                            { key: 'label', label: 'Obligation' },
                            { key: 'authority', label: 'Authority' },
                            { key: 'status', label: '', nowrap: true,
                                render: (r) => <Pill tone={statusTone(r.status)}>{titleCase(r.status)}</Pill> },
                        ]}
                        rows={(detail.deadlines || []).map((d) => ({ ...d, _key: d.deadline_id }))}
                        empty="None yet."
                    />

                    <NoticesSection detail={detail} apiClient={apiClient} onSaved={() => openLease(detail.lease_id)} />

                    {detail.status !== 'ended' && (
                        <div className="mt-6 pt-4 border-t border-slate-100">
                            <Input label="Record move-out — date the tenant actually vacated" type="date"
                                value={form.vacated_on || ''}
                                onChange={(e) => setForm({ ...form, vacated_on: e.target.value })}
                                hint="This is what the itemisation and return clocks run from, and it is not always the lease end date." />
                            <Button variant="danger" disabled={busy || !form.vacated_on}
                                onClick={() => submit(`/api/property-management/leases/${detail.lease_id}/move-out`,
                                    { vacated_on: form.vacated_on },
                                    (r) => {
                                        setModal(null); setDetail(null);
                                        setError({ tone: 'warn', title: 'Move-out recorded — deposit clocks running',
                                            problems: (r.deposit_clocks || []).map((c) => `${c.label}: due ${c.due_on}`) });
                                    })}>
                                Record move-out</Button>
                        </div>
                    )}
                </Modal>
            )}
        </div>
    );
}

// Serving a notice. The required period is computed on the server from the property's
// law stack (Florida's 3-day rent notice counts business days); where the state prescribes
// the wording (Florida, Ch. 83), the statute's text can be filled in and then edited.
const NOTICE_TYPES = [
    ['nonpayment', 'Unpaid rent'], ['lease_violation', 'Lease violation — curable'],
    ['lease_violation_incurable', 'Lease violation — not curable (Florida)'],
    ['termination_month_to_month', 'End a month-to-month tenancy'], ['non_renewal', 'Non-renewal'],
    ['rent_increase', 'Rent increase'], ['deposit_claim', 'Claim on the security deposit (Florida)'],
    ['entry', 'Notice of entry'], ['other', 'Other'],
];
const METHODS = [['personal', 'By hand'], ['posted', 'Left at the residence (tenant absent)'],
    ['certified_mail', 'Certified mail'], ['email', 'E-mail (tenant agreed in writing)'], ['other', 'Other']];

function NoticesSection({ detail, apiClient, onSaved }) {
    const templates = detail.notice_templates || [];
    const types = NOTICE_TYPES.filter(([k]) => !/Florida/.test(NOTICE_TYPES.find((t) => t[0] === k)[1]) || templates.includes(k));
    const [f, setF] = useState({ notice_type: '', served_on: '', method: '', amount: '', detail: '', content: '' });
    const [msg, setMsg] = useState(null);
    const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
    const fill = async () => {
        const q = new URLSearchParams({ type: f.notice_type, amount: f.amount, noncompliance: f.detail, reason: f.detail });
        const t = await apiClient.get(`/api/property-management/leases/${detail.lease_id}/notice-template?${q}`);
        if (t.text) { set('content', t.text); setMsg({ tone: 'info', text: `${t.authority}. ${t.note}` }); }
        else setMsg({ tone: 'info', text: t.note });
    };
    const save = async () => {
        try {
            const r = await apiClient.post(`/api/property-management/leases/${detail.lease_id}/notices`,
                { notice_type: f.notice_type, served_on: f.served_on || null, method: f.method || null,
                  amount: f.amount || null, content: f.content || null });
            setMsg({ tone: 'good', text: `Recorded. ${r.note}${r.effective_on ? ` Runs to ${r.effective_on}.` : ''}` });
            setF({ notice_type: '', served_on: '', method: '', amount: '', detail: '', content: '' });
            onSaved();
        } catch (e) { setMsg({ tone: 'bad', text: e.message }); }
    };
    return (
        <div className="mt-6">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Notices</h4>
            <Table
                columns={[
                    { key: 'served_on', label: 'Served', nowrap: true, render: (r) => fmtDate(r.served_on) },
                    { key: 'notice_type', label: 'Type', render: (r) => titleCase(r.notice_type) },
                    { key: 'required_days', label: 'Period', nowrap: true, render: (r) => (r.required_days ? `${r.required_days} days` : '—') },
                    { key: 'effective_on', label: 'Runs to', nowrap: true, render: (r) => fmtDate(r.effective_on) },
                    { key: 'authority', label: 'Authority' },
                ]}
                rows={(detail.notices || []).map((n) => ({ ...n, _key: n.notice_id }))}
                empty="No notices served."
            />
            <div className="grid sm:grid-cols-4 gap-3 mt-3">
                <Select label="Notice" value={f.notice_type} onChange={(e) => set('notice_type', e.target.value)}
                    options={[{ value: '', label: 'Choose…' }, ...types.map(([v, l]) => ({ value: v, label: l }))]} />
                <Input label="Served on" type="date" value={f.served_on} onChange={(e) => set('served_on', e.target.value)} />
                <Select label="Delivered by" value={f.method} onChange={(e) => set('method', e.target.value)}
                    options={[{ value: '', label: 'Choose…' }, ...METHODS.map(([v, l]) => ({ value: v, label: l }))]} />
                <Input label="Amount (if any)" type="number" value={f.amount} onChange={(e) => set('amount', e.target.value)} />
            </div>
            {templates.includes(f.notice_type) && (
                <div className="flex gap-3 items-end mt-2">
                    <div className="flex-1"><Input label="The noncompliance, or the reason for the claim" value={f.detail} onChange={(e) => set('detail', e.target.value)} /></div>
                    <Button variant="ghost" onClick={fill}>Fill the statute's wording</Button>
                </div>
            )}
            {f.notice_type && <Textarea label="Notice text (kept with the record)" rows={5} value={f.content} onChange={(e) => set('content', e.target.value)} />}
            {msg && <p className={`text-xs mt-1 ${msg.tone === 'bad' ? 'text-red-700' : msg.tone === 'good' ? 'text-emerald-700' : 'text-slate-600'}`}>{msg.text}</p>}
            <Button disabled={!f.notice_type} onClick={save}>Record the notice</Button>
        </div>
    );
}
